const escapeHtml = (value) => String(value ?? '')
  .replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;').replaceAll("'", '&#039;')

const displayValue = (value) => escapeHtml(value === '' || value == null ? '-' : value)

// Real table rows grow with multiline content in browser print engines. Fixed
// table layout and wrapping also keep long codes inside their own field.
const renderFields = (fields, columns = 5, className = '') => {
  const rows = []
  for (let i = 0; i < fields.length; i += columns) {
    const cells = fields.slice(i, i + columns).map(([label, value]) => (
      `<td class="pdf-field"><span>${escapeHtml(label)}</span><strong>${displayValue(value)}</strong></td>`
    ))
    while (cells.length < columns) cells.push('<td class="pdf-field-empty"></td>')
    rows.push(`<tr>${cells.join('')}</tr>`)
  }
  return `<table class="pdf-fields ${className}"><tbody>${rows.join('')}</tbody></table>`
}

export const buildQualityPdfHtml = ({
  title, subtitle, logo, infoRows, qualityRows, samplingRules,
  photos = [], notes = [], signatures = [], systemMm = false,
}) => {
  const samplingRows = samplingRules.map((rule) => `<tr>
    ${rule.format ? `<td rowspan="${rule.rowSpan}" class="group">${escapeHtml(rule.format)}</td>` : ''}
    <td>${escapeHtml(rule.weight)}</td><td>${escapeHtml(rule.pallets)}</td><td>${escapeHtml(rule.samples)}</td>
  </tr>`).join('')
  const photoCells = photos.map(({ label, url }) => `<td class="pdf-photo">
    <span>${escapeHtml(label)}</span>
    ${url ? `<img src="${escapeHtml(url)}" alt="${escapeHtml(label)}" />` : '<div class="pdf-photo-empty">Sin imagen adjunta</div>'}
  </td>`)
  const photoSections = []
  for (let index = 0; index < photoCells.length; index += 2) {
    const cells = photoCells.slice(index, index + 2)
    if (cells.length === 1) cells.push('<td class="pdf-photo-spacer"></td>')
    photoSections.push(`<section class="pdf-section pdf-photo-section"><h2>Fotos de etiquetas${index ? ' (continuacion)' : ''}</h2><table class="pdf-evidence"><tbody><tr>${cells.join('')}</tr></tbody></table></section>`)
  }
  const signatureCells = signatures.map(({ label, name, image, signedAt }) => `<td class="pdf-signature">
    <span>${escapeHtml(label)}: ${displayValue(name)}</span>
    ${signedAt ? `<span class="pdf-signature-date">${escapeHtml(new Date(signedAt).toLocaleString('es-ES'))}</span>` : ''}
    ${image ? `<img src="${escapeHtml(image)}" alt="Firma ${escapeHtml(label)}" />` : '<div class="pdf-signature-empty"></div>'}
    <div class="pdf-signature-line"></div>
  </td>`).join('')

  return `<!doctype html>
<html lang="es"><head><meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>
  @page { size: A4 portrait; margin: 6mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { color: #15191d; font: 9.5px/1.25 Arial, sans-serif; }
  .pdf-page { width: 198mm; max-width: 100%; margin: 0 auto; }
  .pdf-header { display: table; width: 100%; border-bottom: 2px solid #ff7900; padding-bottom: 5px; margin-bottom: 6px; }
  .pdf-title, .pdf-logo-wrap { display: table-cell; vertical-align: middle; }
  .pdf-title { overflow-wrap: anywhere; }
  h1 { margin: 0 0 3px; font-size: 16px; line-height: 1.15; }
  .pdf-title p { margin: 0; color: #68727d; font-weight: 700; }
  .pdf-logo-wrap { width: 60px; text-align: right; }
  .pdf-logo { display: inline-block; width: 52px; height: 46px; object-fit: contain; }
  .pdf-section { border: 1px solid #d8dde3; border-radius: 4px; padding: 5px; margin-bottom: 5px; break-inside: avoid; page-break-inside: avoid; }
  h2 { margin: 0 0 3px; font-size: 11px; line-height: 1.2; }
  table { width: 100%; table-layout: fixed; }
  .pdf-fields, .pdf-evidence { border-collapse: separate; border-spacing: 4px; margin: -2px 0; }
  tr { break-inside: avoid; page-break-inside: avoid; }
  .pdf-field { vertical-align: top; border: 1px solid #e8eaed; border-radius: 3px; padding: 4px; overflow-wrap: anywhere; word-break: normal; }
  .pdf-field span { display: block; color: #68727d; font-size: 7.5px; font-weight: 700; line-height: 1.2; text-transform: uppercase; }
  .pdf-field strong { display: block; margin-top: 2px; font-size: 9.5px; line-height: 1.25; white-space: pre-wrap; overflow-wrap: anywhere; }
  .pdf-notes .pdf-field { height: 12mm; }
  .sampling { border-collapse: collapse; font-size: 8.5px; }
  .sampling th, .sampling td { border: 1px solid #cdd4dc; padding: 2px 4px; text-align: left; vertical-align: top; overflow-wrap: anywhere; }
  .sampling th { background: #f6f7f8; font-weight: 700; }
  .sampling .group { font-weight: 700; vertical-align: middle; }
  .pdf-system-mm { border: 1px solid #cdd4dc; border-radius: 3px; padding: 3px; margin-top: 3px; text-align: center; }
  .pdf-photo, .pdf-signature { vertical-align: top; border: 1px solid #d8dde3; border-radius: 3px; padding: 4px; overflow-wrap: anywhere; }
  .pdf-photo span, .pdf-signature span { display: block; margin-bottom: 3px; font-weight: 700; white-space: pre-wrap; }
  .pdf-photo img { display: block; width: 100%; height: 32mm; object-fit: contain; background: #fbfbfc; }
  .pdf-photo-section.pdf-photo-multiple img { height: 45mm; }
  .pdf-photo-empty { height: 32mm; padding-top: 13mm; color: #68727d; text-align: center; }
  .pdf-signature { height: 30mm; padding: 6px; }
  .pdf-signature .pdf-signature-date { font-size: 7.5px; color: #68727d; font-weight: 400; }
  .pdf-signature img { display: block; width: 100%; max-width: 100%; height: 16mm; object-fit: contain; }
  .pdf-signature-empty { height: 16mm; }
  .pdf-signature-line { margin-top: 4mm; border-top: 1px solid #68727d; }
  @media screen { body { padding: 6mm; } }
</style></head><body>
<main class="pdf-page">
  <header class="pdf-header"><div class="pdf-title"><h1>Control de calidad en produccion</h1><p>${escapeHtml(subtitle)}</p></div><div class="pdf-logo-wrap"><img class="pdf-logo" src="${escapeHtml(logo)}" alt="Clasol" /></div></header>
  <section class="pdf-section"><h2>Datos de pedido</h2>${renderFields(infoRows)}</section>
  <section class="pdf-section"><h2>Producto y validaciones</h2>${renderFields(qualityRows)}${systemMm ? '<div class="pdf-system-mm"><strong>System: mm.</strong></div>' : ''}</section>
  <section class="pdf-section"><h2>Tabla de muestreo</h2><table class="sampling"><thead><tr><th>Confeccion</th><th>Peso ud venta</th><th>N palets/pedido</th><th>N muestras</th></tr></thead><tbody>${samplingRows}</tbody></table></section>
  ${photoSections.map(section => photos.length > 2 ? section.replace('pdf-photo-section', 'pdf-photo-section pdf-photo-multiple') : section).join('')}
  <section class="pdf-section"><h2>Observaciones y firmas</h2>${renderFields(notes, 2, 'pdf-notes')}<table class="pdf-evidence"><tbody><tr>${signatureCells}</tr></tbody></table></section>
</main>
<script>
  window.addEventListener('load', async () => {
    if (document.fonts) await document.fonts.ready;
    await Promise.all(Array.from(document.images, image => image.decode ? image.decode().catch(() => {}) : Promise.resolve()));
    requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
  }, { once: true });
</script>
</body></html>`
}
